/**
 * TransitionSelector Component
 *
 * A comprehensive UI for selecting and configuring slide transitions.
 * Features:
 * - Grid of transition options organized by category
 * - Live mini preview with animation
 * - Duration selector with presets
 * - Easing selector
 * - Apply/Cancel actions
 *
 * Can be used in:
 * - Popover (between filmstrip slides)
 * - Inline in settings panel (compact mode)
 */

import React, { useState, useCallback, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TransitionPreview } from './TransitionPreview';
import {
  TransitionConfig,
  TransitionType,
  TransitionCategory,
  TRANSITION_CATEGORY_LABELS,
  TRANSITION_DURATION_PRESETS,
  TRANSITION_EASING_PRESETS,
  getTransitionsByCategory,
  getTransitionOption,
} from '@/types/videoManifest';
import { Check, X } from 'lucide-react';

export interface TransitionSelectorProps {
  /** Current transition configuration (null for new/default) */
  currentTransition: TransitionConfig | null;
  /** Called when user applies the transition */
  onApply: (transition: TransitionConfig) => void;
  /** Called when user cancels */
  onCancel?: () => void;
  /** URL for the "from" slide thumbnail */
  fromThumbnail?: string;
  /** URL for the "to" slide thumbnail */
  toThumbnail?: string;
  /** Whether this is for editing the default transition */
  isDefault?: boolean;
  /** Compact mode for inline display (no preview, smaller) */
  compact?: boolean;
  /** Additional class names */
  className?: string;
}

export const TransitionSelector: React.FC<TransitionSelectorProps> = ({
  currentTransition,
  onApply,
  onCancel,
  fromThumbnail,
  toThumbnail,
  isDefault = false,
  compact = false,
  className,
}) => {
  // Local state for editing
  const [selectedType, setSelectedType] = useState<TransitionType>(
    currentTransition?.type || 'crossfade'
  );
  const [duration, setDuration] = useState<number>(
    currentTransition?.duration || 0.5
  );
  const [easing, setEasing] = useState<string>(
    currentTransition?.easing || 'power2.inOut'
  );
  const [hoveredType, setHoveredType] = useState<TransitionType | null>(null);

  // Group transitions by category
  const transitionsByCategory = useMemo(() => getTransitionsByCategory(), []);

  // Preview transition config (show hovered or selected)
  const previewTransition: TransitionConfig = useMemo(() => ({
    type: hoveredType || selectedType,
    duration,
    easing,
  }), [hoveredType, selectedType, duration, easing]);

  // Check if changes were made
  const hasChanges = useMemo(() => {
    if (!currentTransition) return true;
    return (
      selectedType !== currentTransition.type ||
      duration !== currentTransition.duration ||
      easing !== currentTransition.easing
    );
  }, [currentTransition, selectedType, duration, easing]);

  // Handle apply
  const handleApply = useCallback(() => {
    onApply({
      type: selectedType,
      duration,
      easing,
    });
  }, [onApply, selectedType, duration, easing]);

  // Render category section
  const renderCategory = (category: TransitionCategory) => {
    const options = transitionsByCategory[category] || [];
    if (options.length === 0) return null;

    return (
      <div key={category} className="space-y-1.5">
        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-0.5">
          {TRANSITION_CATEGORY_LABELS[category]}
        </div>
        <div className={cn(
          'grid gap-1',
          compact ? 'grid-cols-3' : 'grid-cols-3'
        )}>
          {options.map((option) => {
            const isSelected = selectedType === option.type;
            const isHovered = hoveredType === option.type;

            return (
              <button
                key={option.type}
                onClick={() => setSelectedType(option.type)}
                onMouseEnter={() => setHoveredType(option.type)}
                onMouseLeave={() => setHoveredType(null)}
                className={cn(
                  'flex flex-col items-center justify-center gap-0.5 p-2 rounded-md border-2 transition-all duration-150',
                  'hover:bg-accent/50 hover:border-accent',
                  isSelected
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-transparent bg-muted/50 text-muted-foreground',
                  isHovered && !isSelected && 'border-accent/50'
                )}
                title={option.description}
              >
                <span className={cn(
                  'text-base leading-none',
                  compact ? 'text-sm' : 'text-base'
                )}>
                  {option.emoji}
                </span>
                <span className={cn(
                  'font-medium truncate w-full text-center',
                  compact ? 'text-[9px]' : 'text-[10px]'
                )}>
                  {option.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  if (compact) {
    // Compact mode - simpler layout for inline use
    return (
      <div className={cn('space-y-3', className)}>
        {/* Current transition info */}
        <div className="flex items-center gap-2 p-2 rounded-md bg-muted/30">
          <span className="text-lg">
            {getTransitionOption(selectedType)?.emoji || '✦'}
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">
              {getTransitionOption(selectedType)?.label || selectedType}
            </div>
            <div className="text-xs text-muted-foreground">
              {duration}s • {TRANSITION_EASING_PRESETS.find(e => e.value === easing)?.label || easing}
            </div>
          </div>
        </div>

        {/* Type grid */}
        <div className="space-y-2">
          {(['essential', 'directional', 'reveal', 'emphasis'] as TransitionCategory[]).map(renderCategory)}
        </div>

        {/* Duration & Easing row */}
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="text-[10px] font-medium text-muted-foreground uppercase mb-1 block">
              Duration
            </label>
            <Select value={duration.toString()} onValueChange={(v) => setDuration(parseFloat(v))}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRANSITION_DURATION_PRESETS.map((preset) => (
                  <SelectItem key={preset.value} value={preset.value.toString()}>
                    {preset.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <label className="text-[10px] font-medium text-muted-foreground uppercase mb-1 block">
              Easing
            </label>
            <Select value={easing} onValueChange={setEasing}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRANSITION_EASING_PRESETS.map((preset) => (
                  <SelectItem key={preset.value} value={preset.value}>
                    {preset.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Apply button (only if changes made) */}
        {hasChanges && (
          <Button
            size="sm"
            onClick={handleApply}
            className="w-full"
          >
            <Check className="w-3.5 h-3.5 mr-1.5" />
            Apply
          </Button>
        )}
      </div>
    );
  }

  // Full mode - with preview
  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">
          {isDefault ? 'Default Transition' : 'Slide Transition'}
        </h3>
        {onCancel && (
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onCancel}>
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* Preview */}
      <div className="flex justify-center">
        <TransitionPreview
          fromImage={fromThumbnail}
          toImage={toThumbnail}
          transition={previewTransition}
          isActive={true}
          size={{ width: 192, height: 108 }}
          className="shadow-lg"
        />
      </div>

      {/* Transition type grid */}
      <div className="space-y-3 max-h-[200px] overflow-y-auto pr-1">
        {(['essential', 'content-aware', 'directional', 'reveal', 'emphasis'] as TransitionCategory[]).map(renderCategory)}
      </div>

      {/* Duration & Easing */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-medium text-muted-foreground uppercase mb-1 block">
            Duration
          </label>
          <Select value={duration.toString()} onValueChange={(v) => setDuration(parseFloat(v))}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRANSITION_DURATION_PRESETS.map((preset) => (
                <SelectItem key={preset.value} value={preset.value.toString()}>
                  {preset.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] font-medium text-muted-foreground uppercase mb-1 block">
            Easing
          </label>
          <Select value={easing} onValueChange={setEasing}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRANSITION_EASING_PRESETS.map((preset) => (
                <SelectItem key={preset.value} value={preset.value}>
                  {preset.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        {onCancel && (
          <Button variant="outline" size="sm" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
        )}
        <Button
          size="sm"
          onClick={handleApply}
          disabled={!hasChanges}
          className="flex-1"
        >
          <Check className="w-3.5 h-3.5 mr-1.5" />
          Apply
        </Button>
      </div>
    </div>
  );
};

export default TransitionSelector;
